import { floorSortKey } from "./launch-siblings";
import { shortLaunchLabel } from "./launch-stops";

/**
 * Launch menu floor sections (2026-10-20): "Floor 14 › A B C Full Room"
 * instead of a flat list of "14th Floor · A B C Full Room" names, the way
 * Treedis grouped Honeywell's rooms by floor. The floor is read from the
 * model's own name ("14th Floor", "Floor 23", "Basement", "Ground", "L2");
 * the same parser orders the menu (`floorSortKey`). Renaming a model is how
 * the team moves it to another floor.
 */
export type LaunchFloorGroup<T> = {
  key: string;
  label: string;
  members: Array<{ member: T; name: string }>;
};

const FLOOR_WORD = /\b(?:floor|level|storey|story|basement|cellar|ground|lobby|mezz\w*|roof\w*|penthouse)\b|^[lb]\s*\d+$|^\d+(?:st|nd|rd|th)?\s*f$/i;

function floorLabel(key: number): string {
  if (key < 0) return key === -1 ? "Basement" : `Basement ${-key}`;
  if (key === 0) return "Ground floor";
  if (key === 0.5) return "Mezzanine";
  if (key === 999) return "Roof";
  return `Floor ${key}`;
}

/** The model's floor and its name within that floor, or null when it names no floor. */
export function launchFloorOf(label: string): { key: number; label: string; name: string } | null {
  const short = shortLaunchLabel(label).name;
  const parts = short.split(" · ");
  const index = parts.findIndex((part) => FLOOR_WORD.test(part) && floorSortKey(part) !== Number.MAX_SAFE_INTEGER);
  if (index < 0) return null;
  const key = floorSortKey(parts[index]!);
  const rest = parts.filter((_, i) => i !== index).join(" · ");
  return { key, label: floorLabel(key), name: rest || short };
}

/**
 * Sections by floor in the menu's existing order (first appearance), with
 * models that name no floor under "Other spaces". Null when fewer than two
 * floors are named, or when no floor holds two models: one floor, or one
 * model per floor ("Floor 1", "Floor 2"), gains nothing from headings.
 */
export function groupLaunchFloors<T extends { label: string }>(members: readonly T[]): LaunchFloorGroup<T>[] | null {
  const groups = new Map<string, LaunchFloorGroup<T>>();
  for (const member of members) {
    const floor = launchFloorOf(member.label);
    const key = floor ? `floor:${floor.key}` : "other";
    const group = groups.get(key) ?? { key, label: floor ? floor.label : "Other spaces", members: [] };
    group.members.push({ member, name: floor ? floor.name : shortLaunchLabel(member.label).name });
    groups.set(key, group);
  }
  const floors = [...groups.values()].filter((group) => group.key !== "other");
  if (floors.length < 2 || !floors.some((group) => group.members.length >= 2)) return null;
  const other = groups.get("other");
  return other ? [...floors, other] : floors;
}
